// Location: src/components/admin/CommentFeedbackModal.tsx
// Purpose: Modal for editing and providing feedback on AI-generated comments
// Scope: Allows users to edit comments and save feedback for AI learning

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
	X,
	ThumbsDown,
	Edit3,
	Save,
	HelpCircle,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';

interface CommentFeedbackModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	commentId: Id<'twitterXComments'>;
	originalComment: string;
	postText: string;
	socialUsername: string;
}

export function CommentFeedbackModal({
	isOpen,
	onClose,
	onSuccess,
	commentId,
	originalComment,
	postText,
	socialUsername,
}: CommentFeedbackModalProps): React.ReactPortal | null {
	const { user } = useAuth();
	const [editedComment, setEditedComment] = useState(originalComment);
	const [isLoading, setIsLoading] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);

	// Mutation to save feedback
	const rejectCommentMutation = useMutation(api.twitterXComments.rejectComment);

	useEffect(() => {
		if (isOpen) {
			setEditedComment(originalComment);
			setHasChanges(false);
		}
	}, [isOpen, originalComment]);

	useEffect(() => {
		// Check if the edited comment differs from original
		setHasChanges(editedComment.trim() !== originalComment.trim());
	}, [editedComment, originalComment]);

	if (!isOpen) return null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!user?.id) {
			toast({
				title: 'Error',
				description: 'You must be logged in to provide feedback',
				variant: 'destructive',
			});
			return;
		}

		if (!hasChanges) {
			toast({
				title: 'No Changes',
				description: 'Please edit the comment before saving feedback',
				variant: 'destructive',
			});
			return;
		}

		setIsLoading(true);

		try {
			await rejectCommentMutation({
				commentId,
				userId: user.id as unknown as Id<'users'>,
				editedComment: editedComment.trim(),
			});

			toast({
				title: '✅ Feedback Saved',
				description: 'Your edit has been saved to the AI rewriter for training',
				duration: 5000,
			});

			if (onSuccess) {
				onSuccess();
			}

			onClose();
		} catch (err) {
			console.error('Failed to save feedback:', err);
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to save feedback';
			toast({
				title: 'Error',
				description: errorMessage,
				variant: 'destructive',
			});
		} finally {
			setIsLoading(false);
		}
	};

	const modalContent = (
		<div className='fixed inset-0 z-[9999] overflow-hidden'>
			<div
				className='fixed inset-0 bg-black/80 backdrop-blur-sm'
				onClick={onClose}
			/>

			<div className='fixed left-[50%] top-[50%] z-[10000] w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] duration-200'>
				<div className='shadow-[0_0_30px_5px_rgba(236,72,153,0.4)] border border-pink-500/50 bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-900/50 overflow-hidden rounded-2xl'>
					{/* Header */}
					<div className='flex items-center justify-between p-6 border-b border-pink-500/20'>
						<div className='flex items-center gap-3'>
							<div className='p-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-600'>
								<ThumbsDown className='h-6 w-6 text-white' />
							</div>
							<div>
								<h2 className='text-xl font-bold text-white'>
									Edit AI Comment
								</h2>
								<p className='text-pink-500 text-lg font-semibold'>
									@{socialUsername}
								</p>
							</div>
						</div>
						<Button
							variant='ghost'
							size='sm'
							onClick={onClose}
							className='text-pink-300 hover:text-white hover:bg-pink-500/20 transition-all duration-200 h-8 w-8 p-0'
							disabled={isLoading}
						>
							<X className='h-5 w-5' />
						</Button>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit} className='p-6 space-y-6'>
						{/* Original Post Context */}
						<div className='p-4 rounded-xl bg-gradient-to-r from-zinc-800/50 to-zinc-900/50 border border-zinc-700'>
							<Label className='text-sm font-semibold text-zinc-400 mb-2 block'>
								Original Post
							</Label>
							<p className='text-sm text-zinc-300 line-clamp-3'>
								{postText}
							</p>
						</div>

						{/* Original AI Comment */}
						<div className='space-y-3'>
							<div className='flex items-center gap-2'>
								<Label className='text-lg font-semibold text-white'>
									Original AI Comment
								</Label>
								<TooltipProvider delayDuration={0}>
									<Tooltip>
										<TooltipTrigger asChild>
											<div className='h-4 w-4 rounded-full bg-gradient-to-r from-pink-500 to-pink-700 border border-white/20 flex items-center justify-center cursor-help'>
												<HelpCircle className='h-2.5 w-2.5 text-white' />
											</div>
										</TooltipTrigger>
										<TooltipContent className='bg-zinc-800 border-zinc-700 text-white max-w-xs'>
											<p className='text-sm'>
												This is what the AI generated. You can edit
												it below to teach the AI your preferred style.
											</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<div className='p-4 rounded-xl bg-gradient-to-r from-zinc-800/70 to-zinc-900/70 border border-pink-500/30'>
								<p className='text-sm text-zinc-300 whitespace-pre-wrap'>
									{originalComment}
								</p>
							</div>
						</div>

						{/* Edited Comment */}
						<div className='space-y-3'>
							<div className='flex items-center gap-2'>
								<Label className='text-lg font-semibold text-white'>
									<Edit3 className='inline h-4 w-4 mr-2' />
									Your Edited Version
								</Label>
								<TooltipProvider delayDuration={0}>
									<Tooltip>
										<TooltipTrigger asChild>
											<div className='h-4 w-4 rounded-full bg-gradient-to-r from-pink-500 to-pink-700 border border-white/20 flex items-center justify-center cursor-help'>
												<HelpCircle className='h-2.5 w-2.5 text-white' />
											</div>
										</TooltipTrigger>
										<TooltipContent className='bg-zinc-800 border-zinc-700 text-white max-w-xs'>
											<p className='text-sm'>
												Edit the comment to match your style. The AI
												will learn from your edits to generate better
												comments in the future.
											</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<Textarea
								value={editedComment}
								onChange={(e) => setEditedComment(e.target.value)}
								placeholder='Edit the comment here...'
								className='min-h-[120px] text-base bg-gradient-to-r from-zinc-800/70 to-zinc-900/70 border-pink-500/30 hover:border-pink-400/50 focus:border-pink-400 text-white placeholder:text-zinc-400 rounded-xl resize-none'
								disabled={isLoading}
							/>
							{hasChanges && (
								<p className='text-sm text-pink-400'>
									✨ Changes detected - ready to save
								</p>
							)}
						</div>

						{/* Action Buttons */}
						<div className='flex gap-4'>
							<Button
								type='button'
								variant='outline'
								onClick={onClose}
								disabled={isLoading}
								className='flex-1 h-12 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white border-0'
							>
								Cancel
							</Button>
							<Button
								type='submit'
								disabled={isLoading || !hasChanges}
								className='flex-1 h-12 bg-gradient-to-r from-pink-500 via-pink-600 to-purple-600 hover:from-pink-600 hover:via-pink-700 hover:to-purple-700 text-white font-semibold shadow-[0_0_20px_rgba(236,72,153,0.4)] hover:shadow-[0_0_30px_rgba(236,72,153,0.6)] transition-all duration-300'
							>
								{isLoading ? (
									<div className='flex items-center gap-2'>
										<div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
										Saving Feedback...
									</div>
								) : (
									<div className='flex items-center gap-2'>
										<Save className='h-5 w-5' />
										Save to AI Rewriter
									</div>
								)}
							</Button>
						</div>

						{/* Info Text */}
						<p className='text-xs text-center text-zinc-400'>
							💡 Your edits help train the AI to match your writing style.
							<br />
							The more feedback you provide, the better the AI becomes.
						</p>
					</form>
				</div>
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
}